import type { IndustryPage } from "@/lib/data";

export const flooring: IndustryPage = {
  slug: "flooring",
  seoTitle:
    "Flooring Website Design UK | Fixed £1,000 for Floor Fitters",
  seoDescription:
    "Flooring website design for UK fitters: a page per floor type, room galleries, price ranges and local SEO. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for flooring contractors",
  tldr:
    "ConstructionWebDesign.uk offers flooring website design for UK floor fitters and flooring companies: mobile-first sites with a page per floor type, galleries of fitted rooms, your own price ranges and sample and measure requests. The price is £1,000, one-off and fixed, with no subscription. Your website goes live in 7 days from the discovery call, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Flooring website design · UK",
    headline: "Website design for flooring contractors, sorted by floor and room.",
    highlight: "sorted by floor and room",
    sub: "Flooring websites with a page per floor type, a room-by-room gallery of fitted jobs, price per square metre with what's included, sample and measure requests, and separate commercial contract pages.",
    secondaryCta: { label: "See flooring demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/industry-hero/Carpenters%20%26%20Joiners.jpg",
      alt: "Timber boards being cut and finished on a workbench",
      headlineTop: "Win the whole floor,",
      headlineBottom: "not just the fitting.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/joinwright.jpg",
    },
  ],
  demoSlugs: ["joinwright", "renovia", "halden"],
  demosHeading: "Flooring website design demo templates.",
  demosSub:
    "The closest demo templates to flooring work, not client work: joinery, refurbishment and heritage building. Open one to see how a material-led gallery and room-by-room service pages are laid out.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a flooring company need a website?",
    intro:
      "A flooring company needs a website because customers choose a floor by eye and budget for it by the square metre. A flooring company website shows fitted floors in real rooms, sets out your prices and preparation, and takes sample and measure requests while you're on a job.",
    points: [
      {
        title: "Sell by floor type, not by company.",
        body: "Engineered oak, solid wood, LVT and herringbone, laminate, carpet, vinyl sheet, safety flooring and resin. People search for the material, so each material gets its own page, with photos of floors fitted in real rooms rather than a manufacturer's studio shots.",
        icon: "Plank",
      },
      {
        title: "Be clear about price per square metre.",
        body: "Supply-and-fit ranges per square metre, with what's included (uplift and disposal, underlay, beading, subfloor preparation) and what isn't. A clear range lets a customer budget a room before they contact you and compare you fairly with a retailer that prints a figure.",
        icon: "Check",
      },
      {
        title: "Show the preparation, earn the trust.",
        body: "Latex screed, moisture testing, damp-proof membranes, ply overlay and underfloor heating build-ups decide whether a floor lasts. Explaining that work separates a careful floor fitter from a quick lay over a problem subfloor.",
        icon: "Sparkle",
      },
      {
        title: "Keep the commercial side separate.",
        body: "Safety flooring for care homes, sports and gym floors, resin for industrial units and contract carpet tile for offices have different buyers, different standards and different search terms, so each gets its own pages.",
        icon: "Buildings",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does flooring website design include?",
    intro:
      "Flooring website design from ConstructionWebDesign.uk includes a page per floor type, a room-by-room gallery, your price per square metre with what's included, sample and measure requests, subfloor preparation explained and a commercial contract section. Every site is mobile-first, with on-page local SEO for your service areas.",
    items: [
      {
        title: "A page per floor type.",
        body: "Engineered wood, solid oak, LVT and herringbone, laminate, carpet, vinyl and lino, safety flooring, resin, and floor sanding and restoration each get their own page with your photos and your price range. Each page is written around what customers search, such as \"herringbone LVT fitting\" or \"floor sanding\" plus their town.",
      },
      {
        title: "A room-by-room gallery of fitted jobs.",
        body: "Fitted work is sorted by room as well as by material, because customers look for a herringbone kitchen floor or a stair carpet with a landing. Each job shows the material, the area covered, the preparation done and how long it took.",
      },
      {
        title: "Price per square metre, with what's included.",
        body: "Your own supply-and-fit ranges by material, with uplift and disposal, underlay, beading, thresholds and subfloor preparation listed separately, so a customer can budget a room before they contact you. The figures are yours, and nothing goes live that you haven't approved.",
      },
      {
        title: "Sample requests and measure booking.",
        body: "A short form for posted samples, and another to book a free measure if you offer one. Both turn a browser into an enquiry without a phone call, and the measure is where you see the room, check the subfloor and price the real job.",
      },
      {
        title: "Subfloor preparation explained.",
        body: "Moisture testing, latex screed, damp-proof membranes, ply overlay, underfloor heating build-ups and acoustic underlay for flats, written for a customer rather than lifted off a spec sheet. Explaining the preparation shows why your price differs from the cheapest quote.",
      },
      {
        title: "A commercial contract section.",
        body: "Safety flooring and its slip resistance ratings, sports and gym surfaces, resin and industrial floors, and contract carpet tile, with the standards you work to and out-of-hours fitting. Contract Flooring Association (CFA) or National Institute of Carpet and Floorlayers (NICF) membership, or manufacturer-approved fitter status, is shown only where you hold it.",
      },
      {
        title: "Service area pages, designed for a phone first.",
        body: "Every page is designed for a phone first, so a customer can look through fitted floors while standing in the room they want to change. Page titles, headings and a page for each service area name the towns and postcodes you cover, as part of the on-page local SEO.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a flooring website cost in the UK?",
      a: "A flooring website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription. The Website + Brand package costs £1,399 and adds a logo, visual identity and business card mockups. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer and £5,000–£25,000 for an agency, while Wix or Squarespace cost about £22 a month. You own the finished website.",
    },
    {
      q: "How long does it take to build a flooring website?",
      a: "A flooring website from ConstructionWebDesign.uk goes live in 7 days from the discovery call. The work runs in five steps: discovery call, strategy and sitemap, design, build, then launch and support. Having photos of fitted floors, your list of floor types and your price ranges ready for the call keeps the week on track.",
    },
    {
      q: "What should a flooring company website include?",
      a: "A flooring company website should include a page per floor type, a gallery of fitted jobs sorted by room and material, supply-and-fit prices per square metre with inclusions listed, sample and measure requests, an explanation of subfloor preparation, a one-tap call button, reviews and a separate section for commercial contract work. Every page should also work well on a phone.",
    },
    {
      q: "Does a floor fitter need a website?",
      a: "Yes, a floor fitter needs a website, even as a sole trader. Customers comparing an independent fitter with a flooring retailer want to see floors you've fitted, a rough price per square metre and the areas you cover before they ask for a measure. A floor fitter website puts that in one place, so customers aren't left to judge you on a directory listing alone.",
    },
    {
      q: "How does a flooring company website get found on Google?",
      a: "A flooring company website gets found on Google through on-page local SEO, a separate page for each floor type and service area, a complete Google Business Profile and steady customer reviews. Flooring website design from ConstructionWebDesign.uk includes on-page local SEO, so page titles, headings and copy name your floor types and the towns you cover. Nobody can honestly guarantee a ranking, but that structure gives Google clear signals.",
    },
    {
      q: "Should a flooring company publish prices per square metre?",
      a: "Yes, a flooring company can publish prices per square metre as ranges by material, with what's included listed alongside. Customers often compare independent fitters with retailers who print a figure, and a range lets them budget a room while leaving you free to price the actual job after the measure. On a ConstructionWebDesign.uk flooring website, the figures are yours and nothing goes live without your approval.",
    },
    {
      q: "How can an independent flooring company compete with big retailers online?",
      a: "An independent flooring company competes with big retailers online by showing what retailers find hard to show: careful subfloor preparation, fitting quality, the same team measuring and fitting, and photos of real rooms in your own area. A flooring company website from ConstructionWebDesign.uk gives each of those its own section, alongside your floor types and price ranges.",
    },
    {
      q: "Can a flooring website cover both domestic and commercial work?",
      a: "Yes, a flooring website can cover domestic and commercial work on separate pages. A homeowner choosing herringbone LVT wants photos and a price per room, while a facilities manager specifying safety flooring for a care home wants slip resistance ratings, the standards you work to and out-of-hours fitting. Flooring website design from ConstructionWebDesign.uk gives each buyer its own pages.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/local-seo-for-builders",
      label: "Local SEO for builders: rank for \"builder near me\"",
    },
    {
      href: "/blog/online-presence-construction-company",
      label: "Online presence for construction companies: a guide",
    },
  ],
  finalCta: {
    headline: "Talk to us about your flooring website.",
    highlight: "your flooring website",
    sub: "Book a free 30-minute call to talk through your floor types and the areas you cover. Fixed £1,000 price, and your website goes live in 7 days.",
  },
};
